import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { useAppSelector, useAppDispatch } from '@store';
import {
  ajvResolver,
  getResponseErrors,
  ResponseError,
  responseErrorsResolver,
  ValidatorsProps,
} from '@ui/src';
import {
  useEditPSPDetailsMutation,
  useLazyGetPSPDetailsQuery,
  useLazyGetRateSourcesQuery,
} from '../../../settings/store/services/details';
import { EditPSPRatesFormValues, PSPRatesKeys } from '../../../settings/types';
import { setIsDirty } from '../../../settings/store/slices/details';

const useRates = () => {
  const [defaultFormValues, setDefaultFormValues] =
    useState<EditPSPRatesFormValues>();
  const [globalError, setGlobalError] = useState('');
  const [errorList, setErrorList] = useState('');

  const {
    settings: {
      details: { validators, pspDetails, rateSources },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [edit, { isLoading: editIsLoading }] = useEditPSPDetailsMutation();
  const [getDetails, { isLoading }] = useLazyGetPSPDetailsQuery();
  const [getRateSources] = useLazyGetRateSourcesQuery();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    watch,
  } = useForm<EditPSPRatesFormValues>({
    resolver: ajvResolver(validators?.updateSettingSchema as ValidatorsProps),
  });

  const fields = watch();

  const isDirty =
    !isEqual(fields, defaultFormValues) || Boolean(Object.keys(errors).length);

  const getDetailsAction = useCallback(async () => {
    const response = (await getDetails()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const updatedSettings: {
      [key: string]:
        | string
        | boolean
        | number
        | Record<string, string>
        | Record<string, boolean>;
      key: PSPRatesKeys;
    }[] = [];

    if (values.baseRateSource !== defaultFormValues?.baseRateSource) {
      updatedSettings.push({
        baseRateSourceId: values.baseRateSource,
        key: PSPRatesKeys.BASE_RATE_SOURCE,
      });
    }

    if (values.exchangeRateSource !== defaultFormValues?.exchangeRateSource) {
      updatedSettings.push({
        exchangeRateSource: values.exchangeRateSource,
        key: PSPRatesKeys.EXCHANGE_RATE_SOURCE,
      });
    }

    if (updatedSettings.length > 0) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      } else {
        dispatch(setIsDirty(false));
      }

      setDefaultFormValues({
        baseRateSource: values?.baseRateSource || '',
        exchangeRateSource: values?.exchangeRateSource || '',
      });
    }
  });

  useEffect(() => {
    if (!pspDetails) {
      getDetailsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (!rateSources) {
      getRateSources();
    }
  }, [rateSources]);

  useEffect(() => {
    if (pspDetails) {
      const baseRateSource = pspDetails?.find(
        (item) => item.key === 'BASE-RATE-SOURCE',
      );
      const exchangeRateSource = pspDetails?.find(
        (item) => item.key === 'EXCHANGE-RATE-SOURCE',
      );

      reset({
        baseRateSource: (baseRateSource?.data?.value as string) || '',
        exchangeRateSource: (exchangeRateSource?.data?.value as string) || '',
      });

      setDefaultFormValues({
        baseRateSource: (baseRateSource?.data?.value as string) || '',
        exchangeRateSource: (exchangeRateSource?.data?.value as string) || '',
      });
    }
  }, [pspDetails]);

  useEffect(() => {
    dispatch(setIsDirty(isDirty));
  }, [isDirty]);

  return {
    isLoading,
    errorList,
    control,
    errors,
    onSubmit,
    globalError,
    editIsLoading,
    isDirty,
    rateSources,
  };
};

export default useRates;
